import { sessaoEmMemoria } from '@/modulos/sessao/memoria';

import { LICENCAS_DAS_LEITURAS } from './licencas-das-leituras.gerado';

/**
 * A LEITURA SÓ SAI COM A LICENÇA DA ROTA (achado dos robôs no QAS, 20/10/2026).
 *
 * O celular abria telas que disparavam consultas que o perfil não lê:
 * `/manutencao/vencimentos` para o operador e o almoxarifado, uma consulta do
 * estoque para compras, `/compras/pedidos` no combustível do gestor
 * operacional, `/oficina/ordens` na aba Oficina. Cada uma voltava 403 e um
 * toast "Você não tem permissão" sobre uma tela que a pessoa nem pediu para
 * mudar. A tela de Pessoas da mesa resolveu isso consulta a consulta
 * (`src/modulos/pessoas/leitura.ts`); aqui a regra vale para TODA leitura, da
 * mesa e do celular, num lugar só: a camada de pedido confere a sessão contra
 * a licença que o servidor exige daquela rota (a tabela gerada do servidor,
 * `licencas-das-leituras.gerado.ts`) e, sem ela, nem sai — responde ali mesmo o
 * 403 que o servidor responderia, com a licença nomeada, e a tela desenha o
 * estado sem permissão (p.34). Nada chega ao servidor, e nada vira toast
 * (`ehLeituraSemLicenca`, em `cliente-consulta.ts`).
 *
 * Sem sessão conhecida (o primeiro quadro, a tela de entrar) a leitura sai: o
 * servidor continua sendo quem decide, e isto aqui não abre porta nenhuma — só
 * deixa de bater na que está fechada.
 */

/** A frase da recusa local: é por ela que o retorno global sabe que não deve virar toast. */
export const ERRO_DA_LEITURA_SEM_LICENCA = 'o seu perfil não lê esta informação';

interface Padrao {
  readonly partes: readonly string[];
  readonly licenca: string;
  /** Quantas partes são parâmetro (`:id`): a rota literal vence a de parâmetro. */
  readonly parametros: number;
}

const PADROES: readonly Padrao[] = Object.entries(LICENCAS_DAS_LEITURAS).map(([rota, licenca]) => {
  const partes = rota.split('/').filter(Boolean);
  return { partes, licenca, parametros: partes.filter((p) => p.startsWith(':')).length };
});

/** A licença que o servidor exige para LER este caminho, ou `null` (só a sessão, pública ou desconhecida). */
export function licencaDaLeitura(caminho: string): string | null {
  const partes = (caminho.split('?')[0] ?? '').split('/').filter(Boolean);
  let melhor: Padrao | null = null;
  for (const p of PADROES) {
    if (p.partes.length !== partes.length) continue;
    if (!p.partes.every((parte, i) => parte.startsWith(':') || parte === partes[i])) continue;
    if (!melhor || p.parametros < melhor.parametros) melhor = p;
  }
  return melhor?.licenca ?? null;
}

/** A licença que falta para esta leitura, ou `null` quando ela pode sair. */
export function licencaQueFalta(metodo: string | undefined, caminho: string, permissoes: readonly string[] | null): string | null {
  if ((metodo ?? 'GET').toUpperCase() !== 'GET' || permissoes === null) return null;
  const licenca = licencaDaLeitura(caminho);
  return licenca !== null && !permissoes.includes(licenca) ? licenca : null;
}

/**
 * O `fetch` das camadas de pedido: a leitura sem a licença responde ali mesmo
 * o 403 do servidor, com a licença nomeada como o servidor a nomeia — e a
 * camada trata a resposta como trataria a de verdade.
 */
export function buscar(url: string, caminho: string, init?: RequestInit): Promise<Response> {
  const falta = licencaQueFalta(init?.method, caminho, sessaoEmMemoria()?.permissoes ?? null);
  if (falta === null) return fetch(url, init);
  return Promise.resolve(
    new Response(
      JSON.stringify({
        erro: ERRO_DA_LEITURA_SEM_LICENCA,
        comoResolver: `Peça a quem administra o sistema a permissão (${falta}) para o seu perfil.`,
        codigo: 'sem_licenca',
      }),
      { status: 403, headers: { 'content-type': 'application/json' } },
    ),
  );
}

/** A recusa veio de `buscar`, e não do servidor: é estado da tela, nunca toast. */
export function ehLeituraSemLicenca(erro: unknown): boolean {
  const e = erro as { status?: unknown; message?: unknown } | null;
  return e?.status === 403 && e.message === ERRO_DA_LEITURA_SEM_LICENCA;
}
